import { useEffect, useState } from "react";
import SidebarComponent from "../components/common/SidebarComponent";
import HOC from "../components/HOC";
import { doctorConsultationStatus } from "../services/telemedicineApi";
import { useMutation } from "@tanstack/react-query";
import { useNotification } from "../components/context/NotificationContext";
import { readSafeStorage, writeSafeStorage } from "../utils/localStorageHelper";

const DoctorDashboardPage = () => {
  const { showNotification } = useNotification();

  const [isConsultationActive, setIsConsultationActive] = useState(false);

  const { mutate: mutateUpdateStatus, isPending } = useMutation({
    mutationFn: doctorConsultationStatus,
    onSuccess: (_, variables) => {
      showNotification("Berhasil memperbaharui status konsultasi", "success");
      setIsConsultationActive(variables.active);

      const newValue = variables.active.toString();
      writeSafeStorage("is_doctor_active", newValue);

      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "is_doctor_active",
          newValue: newValue,
          storageArea: localStorage,
        }),
      );
    },
    onError: () => {
      showNotification("Gagal memperbaharui status", "error");
    },
  });

  useEffect(() => {
    const savedStatus = readSafeStorage("is_doctor_active", "");
    if (savedStatus !== null) {
      setIsConsultationActive(savedStatus === "true");
    }
  }, []);

  return (
    <div className="flex bg-indigo-50">
      <div className="w-0 lg:w-[96px] h-[100vh] sticky top-0 z-30">
        <SidebarComponent />
      </div>

      <div className="w-[100%] lg:w-[calc(100%-96px)]">
        <div className="h-[calc(100vh-90px)] overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-slate-700 mb-6">
              Dashboard Dokter
            </h1>

            <div className="bg-white rounded-2xl p-6 mb-10">
              <h2 className="text-xl font-semibold text-slate-700 mb-4">
                Pintasan
              </h2>
              <div
                className={`grid ${isConsultationActive ? "grid-cols-3" : "grid-cols-2"} gap-4 mb-6`}
              >
                {isConsultationActive && (
                  <a
                    href="/consultations"
                    className="flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <div className="bg-blue-100 rounded-full p-3 mr-4">
                      <svg
                        className="w-6 h-6 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Lihat Pesan</p>
                      <p className="text-sm text-slate-500">
                        Buka konsultasi aktif
                      </p>
                    </div>
                  </a>
                )}

                <a
                  href="/history"
                  className="flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors cursor-pointer"
                >
                  <div className="bg-green-100 rounded-full p-3 mr-4">
                    <svg
                      className="w-6 h-6 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      Riwayat Konsultasi
                    </p>
                    <p className="text-sm text-slate-500">
                      Lihat konsultasi sebelumnya
                    </p>
                  </div>
                </a>

                <a
                  href="/doctor-profile"
                  className="flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors cursor-pointer"
                >
                  <div className="bg-purple-100 rounded-full p-3 mr-4">
                    <svg
                      className="w-6 h-6 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Profil Saya</p>
                    <p className="text-sm text-slate-500">
                      Kelola informasi profil
                    </p>
                  </div>
                </a>
              </div>

              <h2 className="text-xl font-semibold text-slate-700 mb-4">
                Konsultasi
              </h2>

              <div className="flex items-center justify-between bg-slate-50 border rounded-xl p-4">
                <div>
                  <p className="font-semibold text-slate-700">
                    Status Konsultasi
                  </p>
                  <p className="text-md text-slate-500">
                    {isConsultationActive
                      ? "Dokter dapat menerima konsultasi"
                      : "Dokter tidak bisa menerima konsultasi"}
                  </p>
                </div>

                <button
                  onClick={() => {
                    const newStatus = !isConsultationActive;

                    mutateUpdateStatus({
                      active: newStatus,
                    });
                  }}
                  disabled={isPending}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 ${
                    isConsultationActive ? "bg-green-500" : "bg-gray-300"
                  } ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                      isConsultationActive ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HOC(DoctorDashboardPage, "doctor-required");
